// src/pages/ContactTagsSettingsPage.js — Admin: define, edit, deactivate contact tags
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

const PRESET_COLORS = [
  '#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#6366f1',
  '#8b5cf6', '#ec4899', '#14b8a6', '#06b6d4', '#7c3aed',
  '#6b7280', '#475569',
];

export default function ContactTagsSettingsPage() {
  const { isManagerOrAbove } = useAuth();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTag, setEditingTag] = useState(null);

  useEffect(() => { loadTags(); }, []);

  const loadTags = async () => {
    setLoading(true);
    try {
      const res = await api.get('/contact-tags?include_inactive=true');
      setTags(res.data);
    } catch (err) {
      toast.error('Failed to load tags.');
    } finally {
      setLoading(false);
    }
  };

  const openNewTag = () => { setEditingTag(null); setShowModal(true); };
  const openEditTag = (tag) => { setEditingTag(tag); setShowModal(true); };

  const handleSaved = () => {
    setShowModal(false);
    setEditingTag(null);
    loadTags();
  };

  const handleDelete = async (tag) => {
    if (!window.confirm(`Delete tag "${tag.name}"? This will remove it from ${tag.assignment_count} contact${tag.assignment_count === 1 ? '' : 's'}.`)) return;
    try {
      await api.delete(`/contact-tags/${tag.id}`);
      toast.success('Tag deleted.');
      loadTags();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete tag.');
    }
  };

  const handleToggleActive = async (tag) => {
    try {
      await api.patch(`/contact-tags/${tag.id}`, { isActive: !tag.is_active });
      loadTags();
    } catch (err) {
      toast.error('Failed to update tag.');
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Link to="/contacts" style={{ fontSize: '0.875rem', color: 'var(--primary)' }}>← Back to Contacts</Link>
      </div>

      <div className="page-header">
        <div>
          <h1>Contact Tags</h1>
          <p style={{ color: 'var(--gray-500)', fontSize: '0.9rem', margin: '4px 0 0' }}>
            Categories you can apply to contacts. Used for filtering and audience targeting.
          </p>
        </div>
        {isManagerOrAbove() && (
          <button className="btn btn-primary" onClick={openNewTag}>+ New Tag</button>
        )}
      </div>

      <div className="card">
        <div className="card-body" style={{ padding: 0 }}>
          {loading ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--gray-500)' }}>Loading…</div>
          ) : tags.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--gray-500)' }}>No tags yet.</div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Tag</th>
                  <th>Description</th>
                  <th>Type</th>
                  <th>Contacts</th>
                  <th>Status</th>
                  {isManagerOrAbove() && <th style={{ width: 200 }}></th>}
                </tr>
              </thead>
              <tbody>
                {tags.map(tag => (
                  <tr key={tag.id} style={{ opacity: tag.is_active ? 1 : 0.5 }}>
                    <td>
                      <TagBadge tag={tag} />
                    </td>
                    <td style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>
                      {tag.description || '—'}
                    </td>
                    <td>
                      {tag.is_auto ? (
                        <span className="badge badge-info" style={{ fontSize: '0.7rem' }}>
                          Auto: {tag.auto_rule}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--gray-400)' }}>Manual</span>
                      )}
                    </td>
                    <td style={{ fontWeight: 600 }}>{tag.assignment_count}</td>
                    <td>
                      {tag.is_active
                        ? <span className="badge badge-success">Active</span>
                        : <span className="badge badge-muted">Inactive</span>}
                    </td>
                    {isManagerOrAbove() && (
                      <td>
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                          <button className="btn btn-ghost btn-sm" onClick={() => openEditTag(tag)}>Edit</button>
                          <button className="btn btn-ghost btn-sm" onClick={() => handleToggleActive(tag)}>
                            {tag.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                          {!tag.is_auto && (
                            <button className="btn btn-ghost btn-sm" onClick={() => handleDelete(tag)} style={{ color: 'var(--danger)' }}>
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <TagModal
          tag={editingTag}
          onClose={() => { setShowModal(false); setEditingTag(null); }}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}

// ─── TagBadge ────────────────────────────────────────────────────────────────
export function TagBadge({ tag, onRemove, size = 'md' }) {
  const padding = size === 'sm' ? '2px 8px' : '4px 10px';
  const fontSize = size === 'sm' ? '0.7rem' : '0.78rem';
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding,
      borderRadius: 12,
      background: tag.color + '22',
      color: tag.color,
      border: `1px solid ${tag.color}55`,
      fontSize,
      fontWeight: 600,
      lineHeight: 1.2,
      whiteSpace: 'nowrap',
    }}>
      {tag.name}
      {onRemove && (
        <button onClick={(e) => { e.stopPropagation(); onRemove(); }}
                style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0, fontSize: '0.9rem', lineHeight: 1 }}
                aria-label="Remove tag">×</button>
      )}
    </span>
  );
}

// ─── TagModal ────────────────────────────────────────────────────────────────
function TagModal({ tag, onClose, onSaved }) {
  const isEdit = !!tag;
  const [form, setForm] = useState({
    name:        tag?.name        ?? '',
    color:       tag?.color       ?? '#3b82f6',
    description: tag?.description ?? '',
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Tag name is required.');
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/contact-tags/${tag.id}`, {
          name: form.name.trim(),
          color: form.color,
          description: form.description || null,
        });
      } else {
        await api.post('/contact-tags', {
          name: form.name.trim(),
          color: form.color,
          description: form.description || null,
        });
      }
      toast.success(isEdit ? 'Tag updated.' : 'Tag created.');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <h2>{isEdit ? 'Edit Tag' : 'New Tag'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label className="form-label">Name *</label>
            <input className="form-input" value={form.name}
                   onChange={e => set('name', e.target.value)}
                   maxLength={60}
                   placeholder="e.g. Donor, Politician, VIP" />
            {tag?.is_auto && (
              <small style={{ color: 'var(--warning, #b45309)' }}>
                This is an auto-applied tag. Renaming is OK but the system rule still references it.
              </small>
            )}
          </div>

          <div>
            <label className="form-label">Color</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input type="color" value={form.color}
                     onChange={e => set('color', e.target.value)}
                     style={{ width: 50, height: 36, padding: 2, cursor: 'pointer' }} />
              <input className="form-input" type="text" value={form.color}
                     onChange={e => set('color', e.target.value)}
                     style={{ width: 100, fontFamily: 'monospace' }} />
              <div style={{ display: 'flex', gap: 4 }}>
                {PRESET_COLORS.map(c => (
                  <button key={c}
                          type="button"
                          onClick={() => set('color', c)}
                          style={{
                            width: 24, height: 24, borderRadius: 4,
                            background: c,
                            border: form.color.toLowerCase() === c.toLowerCase() ? '2px solid #1f2937' : '1px solid #d1d5db',
                            cursor: 'pointer',
                          }}
                          aria-label={`Use color ${c}`} />
                ))}
              </div>
            </div>
            <div style={{ marginTop: 8 }}>
              <small style={{ color: 'var(--gray-500)' }}>Preview: </small>
              <TagBadge tag={{ name: form.name || 'Tag Name', color: form.color }} />
            </div>
          </div>

          <div>
            <label className="form-label">Description (optional)</label>
            <textarea className="form-input" value={form.description}
                      onChange={e => set('description', e.target.value)}
                      rows={2}
                      placeholder="What this tag means or when to apply it" />
          </div>
        </div>
        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : (isEdit ? 'Save' : 'Create')}
          </button>
        </div>
      </div>
    </div>
  );
}
